import {
  Button,
  CardBackground,
  CardItem,
  Tabs,
  Typography,
} from "@hellocarbo/lasagne";
import { Building, Building2, Layers, Menu, Plus } from "lucide-react";
import { ReactNode, use } from "react";
import { Trans, useTranslation } from "react-i18next";

import { FeaturesContext } from "@/contexts";
import { ConsolidatedReview as TConsolidatedReview, ScopeTypes } from "@/types";
import { formatCarbon } from "@/utils/carbon";
import { formatCategoriesForPieCharts } from "@/utils/consolidation";
import { formatEndDate } from "@/utils/formatDate";
import {
  calculateEconomicCarbonIntensity,
  calculateSocialCarbonIntensity,
} from "@/utils/misc";

import CarbonIntensity from "./CarbonIntensity";
import ConsolidationPie from "./ConsolidationPie";
import ReviewByCategories from "./ReviewByCategories";
import ReviewByScopes, { Scopes } from "./ReviewByScopes";
import ReviewBySubsidiaries from "./ReviewBySubsidiaries";

function Consolidation({
  consolidatedReview,
  isMultientityDashboard = false,
  deliverables,
}: {
  consolidatedReview: TConsolidatedReview;
  isMultientityDashboard?: boolean;
  deliverables?: ReactNode;
}) {
  const { t } = useTranslation(["common", "dashboard", "formatter"]);
  const { featureFlags } = use(FeaturesContext);
  const isMultientityDashboardEnabled = Boolean(
    featureFlags?.["multientity_dashboard"]
  );

  if (!consolidatedReview) {
    return (
      <div className="grid place-items-center">
        <p>{t("common:loading")}</p>
      </div>
    );
  }

  const {
    company_has_subsidiaries: companyHasSubsidiaries,
    carbon,
    turnover,
    employees,
    review_categories: categoriesData,
    scopes,
    subsidiaries,
    period_start,
    period_end,
  } = consolidatedReview;

  const isConsolidated =
    companyHasSubsidiaries &&
    (!isMultientityDashboardEnabled || isMultientityDashboard);
  const chartCategories = formatCategoriesForPieCharts(categoriesData);

  const TabsWrapper = isMultientityDashboard ? "div" : CardBackground;

  const { formattedCarbon: formattedCarbonTotal, unit: totalUnit } =
    formatCarbon(t, carbon);

  const header = (
    <div
      className={
        isMultientityDashboard ? "gap-x-sm flex flex-wrap items-baseline" : ""
      }
    >
      <Typography
        step="2"
        as="h2"
        weight="bold"
        className={isMultientityDashboard ? "" : "mb-2xs"}
      >
        {isConsolidated
          ? t("dashboard:consolidation.subsidiaries_subtitle")
          : t("dashboard:consolidation.subtitle")}
      </Typography>
      <Typography step="0">
        <Trans
          t={t}
          i18nKey="dashboard:consolidation.dates"
          values={{
            from: t("formatter:date", {
              value: new Date(period_start),
            }),
            to: t("formatter:date", {
              value: formatEndDate(new Date(period_end)),
            }),
          }}
          components={{
            1: <span className="font-bold" />,
          }}
        />
      </Typography>
    </div>
  );

  const charts = (
    <>
      <CardItem className="lg:shadow-xl">
        {isMultientityDashboard ? null : <div className="mb-lg">{header}</div>}
        <div className="gap-x-xl gap-y-md grid grid-cols-[1fr_2fr] lg:flex lg:h-full lg:flex-col">
          <div className="min-h-[250px]">
            <ConsolidationPie
              data={chartCategories}
              title={formattedCarbonTotal}
              titleSubText={t(`common:unit.${totalUnit}`)}
              extraSeriesParams={{
                radius: ["50%", "90%"],
              }}
            />
          </div>
          <div className="gap-y-sm flex flex-col">
            <CarbonIntensity
              title={t("common:carbon_intensity.economic.title")}
              value={
                turnover
                  ? calculateEconomicCarbonIntensity(carbon, turnover)
                  : undefined
              }
              unit={t("common:unit.carbon_intensity.economic")}
              emptyValueMessage={t(
                "common:carbon_intensity.economic.emptyValueMessage"
              )}
            />
            <CarbonIntensity
              title={t("common:carbon_intensity.social.title")}
              value={
                employees
                  ? calculateSocialCarbonIntensity(carbon, employees)
                  : undefined
              }
              unit={t("common:unit.carbon_intensity.social")}
              emptyValueMessage={t(
                "common:carbon_intensity.social.emptyValueMessage"
              )}
            />
          </div>
        </div>
      </CardItem>
      <TabsWrapper className="min-h-[500px]">
        <Tabs
          defaultValue="categories"
          listClassName={isMultientityDashboard ? "flex-1" : undefined}
          extraContent={
            !isConsolidated && consolidatedReview.id ? (
              <Button
                as="a"
                href={`/reviews/${consolidatedReview.id}`}
                shape="link"
                intent="secondary"
                SuffixIcon={Plus}
              >
                {t("dashboard:consolidation.seeMore")}
              </Button>
            ) : null
          }
          tabs={[
            {
              label: t("dashboard:consolidation.views.categories"),
              value: "categories",
              PrefixIcon: Layers,
              content: <ReviewByCategories categories={chartCategories} />,
            },
            ...(isMultientityDashboard
              ? [
                  {
                    label: t("dashboard:consolidation.views.scopes_beges"),
                    value: "scopes_beges",
                    PrefixIcon: Building,
                    disabled: !scopes,
                    content: scopes ? (
                      <Scopes
                        scopesData={scopes.beges}
                        type={ScopeTypes.beges}
                        isMultientityDashboard
                      />
                    ) : null,
                  },
                  {
                    label: t("dashboard:consolidation.views.scopes_ghg"),
                    value: "scopes_ghg",
                    PrefixIcon: Building2,
                    disabled: !scopes,
                    content: scopes ? (
                      <Scopes
                        scopesData={scopes.ghg_protocol}
                        type={ScopeTypes.ghg}
                        isMultientityDashboard
                      />
                    ) : null,
                  },
                ]
              : [
                  {
                    label: t("dashboard:consolidation.views.scopes"),
                    value: "scopes",
                    PrefixIcon: Building,
                    disabled: !scopes,
                    content: scopes ? <ReviewByScopes scopes={scopes} /> : null,
                  },
                ]),
            ...(isMultientityDashboardEnabled
              ? []
              : [
                  {
                    label: t("dashboard:consolidation.views.subsidiaries"),
                    value: "subsidiaries",
                    PrefixIcon: Menu,
                    disabled: !subsidiaries,
                    content: subsidiaries ? (
                      <ReviewBySubsidiaries
                        subsidiaries={subsidiaries}
                        total={carbon}
                      />
                    ) : null,
                  },
                ]),
          ]}
        />
      </TabsWrapper>
    </>
  );

  if (isMultientityDashboard) {
    return (
      <CardBackground className="gap-y-md flex flex-col">
        {header}
        {deliverables}
        <div className="gap-x-sm gap-y-md grid grid-cols-1 lg:grid-cols-[1fr_2fr]">
          {charts}
        </div>
      </CardBackground>
    );
  }

  return (
    <CardBackground className="gap-x-sm gap-y-md grid grid-cols-1 bg-transparent p-0! lg:grid-cols-[1fr_2fr] lg:bg-white">
      {charts}
    </CardBackground>
  );
}

export default Consolidation;
